"use client";

import { c as _c } from "react-compiler-runtime";
import { Copy, RotateCcw, Wrench } from "lucide-react";
import { usePathname, useRouter } from "next/navigation";
import { useEffect, useMemo, useSyncExternalStore } from "react";
import { FormProvider, useWatch } from "react-hook-form";
import { z } from "zod";
import { SelectField, TextField, useFormProvider } from "@/components/form";
import { Button } from "@/components/primitives";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { queryClient } from "@/core/caching";
import { EXPERIMENTS, useExperiment } from "@/core/experimentation";
import { isDebugAssessmentPanelDomainAllowed } from "@/core/host";
import { languages } from "@/core/i18n";
import { toast } from "@/core/notifications";
import { cn } from "@/core/utils";
import { AgelessAssessmentFlowType, AgelessAssessmentStep, AgelessAssessmentUsageContext, type DisplayAgelessAssessment, TreatmentExperienceLevel } from "@orval/model";
import { assessmentSessionStorageService } from "../../AssessmentSessionStorageService";
import { resetAssessment } from "../../assessmentLifecycle";
import { getScopedCurrentAssessmentQueryKey } from "../../currentAssessmentCache";
import { buildAssessmentStepUrl, resolveFlowType } from "../../assessment.utils.server";
import { getAssessmentSessionQueryKey } from "../../AssessmentSessionProvider";
import { FLOW_SLUG_MAP } from "../../assessments.config";
import { getStepMetaForFlow } from "../../steps/assessment.step-config";
const LANG_OPTIONS = languages.map(lang => ({
  label: lang,
  value: lang
}));
const debugAssessmentSchema = z.object({
  lang: z.enum(languages as unknown as [string, ...string[]], {
    required_error: "Language is required"
  }),
  subdomainSlug: z.string().min(1, "Subdomain is required"),
  flowSlug: z.enum(["try-on", "wellness", "transformation"]),
  stepSlug: z.string().min(1, "Step is required")
});
type DebugAssessmentForm = z.infer<typeof debugAssessmentSchema>;
export type DebugAssessmentPanelProps = {
  /**
   * Optional styling hook (useful when embedded in other layouts).
   * The component is fixed-positioned by default.
   */
  className?: string;
};
export default function DebugAssessmentPanel(t0) {
  const $ = _c(79);
  const {
    className
  } = t0;
  const router = useRouter();
  const pathname = usePathname();
  const isAllowedDomain = useSyncExternalStore(_temp, _temp2, _temp3);
  let t1;
  if ($[0] !== pathname) {
    t1 = getDefaultsFromPathname(pathname);
    $[0] = pathname;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const defaultsFromPath = t1;
  let t2;
  if ($[2] !== router) {
    t2 = values => {
      const url = buildUrl(values);
      router.push(url);
      return Promise.resolve();
    };
    $[2] = router;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== defaultsFromPath || $[5] !== t2) {
    t3 = {
      defaultValues: defaultsFromPath,
      schema: debugAssessmentSchema,
      onSubmit: t2,
      showValidationErrorToast: true,
      validationErrorToastMessage: "Please fix the debug form errors",
      showErrorToast: true,
      errorToastMessage: "Failed to navigate"
    };
    $[4] = defaultsFromPath;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const form = useFormProvider(t3);
  let t4;
  if ($[7] !== form.methods.control) {
    t4 = {
      control: form.methods.control,
      name: "flowSlug"
    };
    $[7] = form.methods.control;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const flowSlug = useWatch(t4);
  let t5;
  if ($[9] !== form.methods.control) {
    t5 = {
      control: form.methods.control,
      name: "stepSlug"
    };
    $[9] = form.methods.control;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const stepSlug = useWatch(t5);
  let t6;
  if ($[11] !== form.methods.control) {
    t6 = {
      control: form.methods.control
    };
    $[11] = form.methods.control;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const watchedValues = useWatch(t6) as DebugAssessmentForm;
  let t7;
  if ($[13] !== flowSlug) {
    t7 = resolveFlowType(flowSlug) ?? AgelessAssessmentFlowType.Aesthetics;
    $[13] = flowSlug;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const flowType = t7;
  let t8;
  if ($[15] !== flowType) {
    t8 = getStepMetaForFlow(flowType).map(_temp4);
    $[15] = flowType;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const stepOptions = t8;
  let t9;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = [{
      label: "Aesthetics",
      value: "try-on"
    }, {
      label: "Wellness",
      value: "wellness"
    }, {
      label: "Aesthetics + Wellness",
      value: "transformation"
    }];
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const flowOptions = t9 as const;
  let t10;
  let t11;
  if ($[18] !== form.methods || $[19] !== stepOptions || $[20] !== stepSlug) {
    t10 = () => {
      const hasStep = stepOptions.some(opt => opt.value === stepSlug);
      if (hasStep) {
        return;
      }
      const firstStep = stepOptions[0]?.value ?? "start";
      form.methods.setValue("stepSlug", firstStep, {
        shouldDirty: true,
        shouldTouch: true
      });
    };
    t11 = [form.methods, stepOptions, stepSlug];
    $[18] = form.methods;
    $[19] = stepOptions;
    $[20] = stepSlug;
    $[21] = t10;
    $[22] = t11;
  } else {
    t10 = $[21];
    t11 = $[22];
  }
  useEffect(t10, t11);
  let t12;
  let t13;
  if ($[23] !== defaultsFromPath || $[24] !== form.methods) {
    t12 = () => {
      form.methods.reset(defaultsFromPath);
    };
    t13 = [defaultsFromPath, form.methods];
    $[23] = defaultsFromPath;
    $[24] = form.methods;
    $[25] = t12;
    $[26] = t13;
  } else {
    t12 = $[25];
    t13 = $[26];
  }
  useEffect(t12, t13);
  let t14;
  try {
    let t15;
    if ($[27] !== watchedValues) {
      t15 = buildUrl(watchedValues);
      $[27] = watchedValues;
      $[28] = t15;
    } else {
      t15 = $[28];
    }
    t14 = t15;
  } catch {
    t14 = "";
  }
  const previewUrl = t14;
  let t15;
  if ($[29] !== form.methods) {
    t15 = async () => {
      const currentValues = form.methods.getValues();
      const url_0 = buildUrl(currentValues);
      try {
        await navigator.clipboard.writeText(url_0);
        toast.info("Copied assessment link");
      } catch {
        toast.error("Copy failed");
      }
    };
    $[29] = form.methods;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  const copyUrl = t15;
  let t16;
  if ($[31] !== form.methods || $[32] !== router) {
    t16 = async () => {
      const currentValues_0 = form.methods.getValues();
      const resolvedFlow = FLOW_SLUG_MAP[currentValues_0.flowSlug] ?? AgelessAssessmentFlowType.Aesthetics;
      const flowSteps = getStepMetaForFlow(resolvedFlow);
      const stepMeta = flowSteps.find(step_0 => step_0.slug === currentValues_0.stepSlug);
      const targetStep = stepMeta?.id ?? flowSteps[0].id;
      const debugToken = `debug-token:${currentValues_0.subdomainSlug}:${resolvedFlow}`;
      await resetAssessment(currentValues_0.subdomainSlug, resolvedFlow);
      assessmentSessionStorageService.setAuthToken({
        subdomainSlug: currentValues_0.subdomainSlug,
        flowType: resolvedFlow
      }, debugToken);
      queryClient.setQueryData(getAssessmentSessionQueryKey(currentValues_0.subdomainSlug, resolvedFlow), debugToken);
      const mockAssessment = buildDebugMockAssessment({
        subdomainSlug: currentValues_0.subdomainSlug,
        flowType: resolvedFlow,
        currentStep: targetStep
      });
      queryClient.setQueryData(getScopedCurrentAssessmentQueryKey(`Bearer ${debugToken}`), mockAssessment);
      router.push(buildUrl(currentValues_0));
      toast.info("Seeded mock assessment data");
    };
    $[31] = form.methods;
    $[32] = router;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  const seedMockDataAndJump = t16;
  if (!isAllowedDomain) {
    return null;
  }
  let t17;
  if ($[34] !== className) {
    t17 = cn("fixed right-4 bottom-4 z-50", className);
    $[34] = className;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <SheetTrigger asChild={true}><Button className="rounded-full shadow-lg" size="lg" type="button" variant="outline"><Wrench className="h-4 w-4" />Debug</Button></SheetTrigger>;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  let t19;
  let t20;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <SheetHeader><SheetTitle>Debug: Jump to assessment flow</SheetTitle><SheetDescription>Quick navigation helper (non-production domains only).</SheetDescription></SheetHeader>;
    t20 = <ExperimentSmokeTestRow />;
    $[37] = t19;
    $[38] = t20;
  } else {
    t19 = $[37];
    t20 = $[38];
  }
  let t21;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <SelectField.RHF label="Language" name="lang" options={LANG_OPTIONS} placeholder="Select a language" valueOnly={true} />;
    $[39] = t21;
  } else {
    t21 = $[39];
  }
  let t22;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <TextField.RHF hint="Matches the platform subdomain slug used by the API" inputProps={{
      autoComplete: "off",
      placeholder: "amora"
    }} label="Platform (subdomain)" name="subdomainSlug" />;
    $[40] = t22;
  } else {
    t22 = $[40];
  }
  let t23;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <SelectField.RHF hint="Choose which assessment flow to open" label="Flow" name="flowSlug" options={[...flowOptions]} placeholder="Select a flow" valueOnly={true} />;
    $[41] = t23;
  } else {
    t23 = $[41];
  }
  let t24;
  if ($[42] !== stepOptions) {
    t24 = <SelectField.RHF hint="Jump to a specific step within the flow" label="Step" name="stepSlug" options={stepOptions} placeholder="Select a step" valueOnly={true} />;
    $[42] = stepOptions;
    $[43] = t24;
  } else {
    t24 = $[43];
  }
  let t25;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <div className="font-medium">Preview</div>;
    $[44] = t25;
  } else {
    t25 = $[44];
  }
  const t26 = previewUrl || "\u2014";
  let t27;
  if ($[45] !== t26) {
    t27 = <div className="type-caption bg-muted/30 rounded-md border p-3">{t25}<div className="text-muted-foreground mt-1 break-all">{t26}</div></div>;
    $[45] = t26;
    $[46] = t27;
  } else {
    t27 = $[46];
  }
  let t28;
  if ($[47] !== copyUrl) {
    t28 = () => void copyUrl();
    $[47] = copyUrl;
    $[48] = t28;
  } else {
    t28 = $[48];
  }
  let t29;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <Copy className="h-4 w-4" />;
    $[49] = t29;
  } else {
    t29 = $[49];
  }
  let t30;
  if ($[50] !== t28) {
    t30 = <Button type="button" variant="outline" onClick={t28}>{t29}Copy link</Button>;
    $[50] = t28;
    $[51] = t30;
  } else {
    t30 = $[51];
  }
  let t31;
  if ($[52] !== seedMockDataAndJump) {
    t31 = <Button type="button" variant="outline" onClick={seedMockDataAndJump}>Seed mock data + jump</Button>;
    $[52] = seedMockDataAndJump;
    $[53] = t31;
  } else {
    t31 = $[53];
  }
  let t32;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <Button type="submit" variant="default">Jump</Button>;
    $[54] = t32;
  } else {
    t32 = $[54];
  }
  let t33;
  if ($[55] !== t30 || $[56] !== t31) {
    t33 = <div className="flex flex-col gap-2 sm:gap-2">{t30}{t31}{t32}</div>;
    $[55] = t30;
    $[56] = t31;
    $[57] = t33;
  } else {
    t33 = $[57];
  }
  let t34;
  if ($[58] !== form.triggerSubmit || $[59] !== t24 || $[60] !== t27 || $[61] !== t33) {
    t34 = <form className="mt-6 space-y-4" onSubmit={form.triggerSubmit}>{t21}{t22}{t23}{t24}{t27}{t33}</form>;
    $[58] = form.triggerSubmit;
    $[59] = t24;
    $[60] = t27;
    $[61] = t33;
    $[62] = t34;
  } else {
    t34 = $[62];
  }
  let t35;
  if ($[63] !== form.methods || $[64] !== t34) {
    t35 = <FormProvider {...form.methods}>{t34}</FormProvider>;
    $[63] = form.methods;
    $[64] = t34;
    $[65] = t35;
  } else {
    t35 = $[65];
  }
  let t36;
  if ($[66] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = <div className="type-body-sm text-muted-foreground font-medium">Quick actions</div>;
    $[66] = t36;
  } else {
    t36 = $[66];
  }
  let t37;
  if ($[67] !== form.methods || $[68] !== router) {
    t37 = () => {
      const currentValues_1 = form.methods.getValues();
      const resolvedFlow_0 = FLOW_SLUG_MAP[currentValues_1.flowSlug] ?? AgelessAssessmentFlowType.Aesthetics;
      resetAssessment(currentValues_1.subdomainSlug, resolvedFlow_0);
      const steps = getStepMetaForFlow(resolvedFlow_0);
      const firstStep_0 = steps[0];
      const url_1 = buildAssessmentStepUrl(currentValues_1.lang, resolvedFlow_0, currentValues_1.subdomainSlug, firstStep_0.slug);
      router.push(url_1);
      toast.info("Assessment state reset");
    };
    $[67] = form.methods;
    $[68] = router;
    $[69] = t37;
  } else {
    t37 = $[69];
  }
  let t38;
  if ($[70] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = <RotateCcw className="h-4 w-4" />;
    $[70] = t38;
  } else {
    t38 = $[70];
  }
  let t39;
  if ($[71] !== t37) {
    t39 = <div className="mt-6 border-t pt-4">{t36}<div className="mt-2 flex flex-col gap-2"><Button className="w-full justify-start" type="button" variant="outline" onClick={t37}>{t38}{"Reset & go to first step"}</Button></div></div>;
    $[71] = t37;
    $[72] = t39;
  } else {
    t39 = $[72];
  }
  let t40;
  if ($[73] !== t35 || $[74] !== t39) {
    t40 = <Sheet>{t18}<SheetContent className="w-[420px] max-w-[95vw]" side="right">{t19}{t20}{t35}{t39}</SheetContent></Sheet>;
    $[73] = t35;
    $[74] = t39;
    $[75] = t40;
  } else {
    t40 = $[75];
  }
  let t41;
  if ($[76] !== t17 || $[77] !== t40) {
    t41 = <div className={t17}>{t40}</div>;
    $[76] = t17;
    $[77] = t40;
    $[78] = t41;
  } else {
    t41 = $[78];
  }
  return t41;
}
function _temp4(step) {
  return {
    label: `${step.label} (${step.slug})`,
    value: step.slug
  };
}
function _temp3() {
  return false;
}
function _temp2() {
  return isDebugAssessmentPanelDomainAllowed();
}
function _temp(onStoreChange) {
  const timeoutId = window.setTimeout(onStoreChange, 0);
  return () => window.clearTimeout(timeoutId);
}
function buildUrl(values: DebugAssessmentForm): string {
  const flowType = FLOW_SLUG_MAP[values.flowSlug] ?? AgelessAssessmentFlowType.Aesthetics;
  return buildAssessmentStepUrl(values.lang, flowType, values.subdomainSlug, values.stepSlug);
}
type BuildDebugMockAssessmentParams = {
  subdomainSlug: string;
  flowType: AgelessAssessmentFlowType;
  currentStep: AgelessAssessmentStep;
};
function buildDebugMockAssessment({
  subdomainSlug,
  flowType,
  currentStep
}: BuildDebugMockAssessmentParams): DisplayAgelessAssessment {
  const nowIso = new Date().toISOString();
  const completedAtIso = new Date(Date.now() + 60_000).toISOString();
  const mockId = `debug-assessment:${subdomainSlug}:${flowType}`;
  return {
    id: mockId,
    anonymousId: `debug-anon:${subdomainSlug}`,
    context: AgelessAssessmentUsageContext.Standard,
    includeBeautyScoreInResults: true,
    includeFutureSelfInResults: true,
    // The provider AI-recommend opt-in is an at-practice concept; the debug panel mocks a
    // Standard consumer assessment, where it is never set.
    useAiRecommendedTreatments: false,
    platformId: `debug-platform:${subdomainSlug}`,
    flowType,
    currentStep,
    refusedImageGenerationKinds: [],
    firstName: "Alex",
    utmSource: "debug",
    utmMedium: "panel",
    utmCampaign: "seed-mock-data",
    harmonyScore: 79,
    potentialScore: 91,
    skinAge: 33,
    uploadedFrontFaceImage: `debug-front-image:${subdomainSlug}`,
    frontFaceImageUrl: "https://placehold.co/900x1200/png?text=Front+Face",
    frontFaceImageContentType: "image/png",
    selectedProfileFacialImage: `debug-profile-image:${subdomainSlug}`,
    selectedProfileSide: "right",
    profileFaceImageUrl: "https://placehold.co/900x1200/png?text=Profile+Face",
    profileFaceImageContentType: "image/png",
    userAge: 38,
    treatmentExperienceLevel: TreatmentExperienceLevel.Somewhat,
    primaryConcerns: ["fine_lines", "texture", "tone"],
    previousTreatmentCategories: ["injectables", "laser"],
    wellnessInterests: ["weight_management", "muscle_toning"],
    isReturningPatient: true,
    processingComplete: true,
    enhancedPhotoUrl: "https://placehold.co/900x1200/png?text=Enhanced+Front",
    enhancedPhotoContentType: "image/png",
    enhancedProfilePhotoUrl: "https://placehold.co/900x1200/png?text=Enhanced+Profile",
    enhancedProfilePhotoContentType: "image/png",
    futureSelfWithTreatmentImageUrl: "https://placehold.co/900x1200/png?text=Future+Self+With+Treatment",
    futureSelfWithTreatmentImageContentType: "image/png",
    futureSelfWithoutTreatmentImageUrl: "https://placehold.co/900x1200/png?text=Future+Self+Without+Treatment",
    futureSelfWithoutTreatmentImageContentType: "image/png",
    futureSelfWithTreatmentYearsYounger: 6,
    estimatedInvestmentMin: 1200,
    estimatedInvestmentMax: 4200,
    overallRecommendation: "Focus on collagen support, medical weight management, and a progressive treatment cadence.",
    remainingTransformationEdits: 5,
    selectedTreatments: [{
      productCapabilityId: "capability-semiglutide",
      intensity: 68,
      enabled: true
    }, {
      productCapabilityId: "capability-b12-mic",
      intensity: 58,
      enabled: true
    }],
    // Mirrors what the server computes for this mock's shape: the aesthetic analysis below is
    // present, so AgelessAssessment.HasGeneratedResults() would report true; a Standard-context
    // assessment is never a provider's delivery to make, so HasUnsentConsultResults() is false.
    hasGeneratedResults: true,
    hasUnsentConsultResults: false,
    // The provider-edit readiness fields feed Admin's consult editor; no consumer surface reads them,
    // so this mock does not model them.
    includedDomains: [],
    resultsComplete: false,
    isReadyForProviderEdit: false,
    comprehensiveTreatmentAnalysis: {
      beautyScore: {
        score: 79,
        potentialScore: 91,
        summary: {
          translations: []
        },
        positives: [],
        negatives: [],
        pillars: {}
      },
      skinAge: {
        estimatedAge: 33,
        potentialAge: 29,
        summary: {
          translations: []
        },
        potentialSummary: {
          translations: []
        }
      },
      treatments: {
        overallPlanFraming: "Structured monthly plan with progressive checkpoints.",
        overallRecommendation: {
          translations: []
        },
        recommendations: []
      }
    },
    enhancedImageRecalculatedBeautyScoreDetail: {
      score: 90,
      potentialScore: 93,
      summary: {
        translations: []
      },
      positives: [],
      negatives: []
    },
    enhancedImageRecalculatedSkinAge: 28,
    agingReport: {
      yearsYoungerEstimate: 6,
      headline: "Your projected aging trajectory improves with treatment adherence.",
      agingScience: "Personalized model indicates visible skin age reduction potential.",
      treatmentBenefits: ["Improved skin texture and tone", "Reduced fine lines", "Better long-term collagen support"],
      withoutTreatmentPreview: "Natural progression with moderate volume loss and texture decline.",
      withTreatmentPreview: "Improved texture retention and lower projected skin age.",
      keyInsights: [{
        title: "Volume retention",
        description: "Consistent treatment can preserve facial volume over time.",
        icon: "sparkles"
      }],
      futureRecommendations: [{
        name: "Maintenance Neuromodulator",
        reason: "Helps prevent deeper dynamic lines from forming.",
        recommendedAge: "Starting at 35"
      }]
    },
    personId: `debug-person:${subdomainSlug}`,
    hasRedeemedPlatformReward: false,
    hasVerifiedInfo: true,
    smsConsent: true,
    transactionalConsent: true,
    createdAt: nowIso,
    completedAt: completedAtIso,
    comprehensiveAnalysisProcessingComplete: true,
    wellnessOriginalImageUrl: "https://placehold.co/900x1200/png?text=Wellness+Original",
    wellnessWeightLossImageUrl: "https://placehold.co/900x1200/png?text=Wellness+Weight+Loss",
    wellnessMuscleGainImageUrl: "https://placehold.co/900x1200/png?text=Wellness+Muscle+Gain",
    wellnessCombinedImageUrl: "https://placehold.co/900x1200/png?text=Wellness+Combined",
    wellnessTreatmentAnalysis: {
      weightLossRecommendations: {
        topPick: {
          treatment: "Semaglutide",
          category: "Weight Management",
          why: "Supports appetite regulation and sustained caloric control.",
          howItWorks: "Weekly GLP-1 therapy protocol",
          priceEstimate: "$350/mo"
        },
        alternatives: []
      },
      muscleRecommendations: {
        topPick: {
          treatment: "B12 + MIC",
          category: "Muscle Support",
          why: "Supports lean mass preservation during fat-loss phases.",
          howItWorks: "Metabolic support injections",
          priceEstimate: "$180/mo"
        },
        alternatives: []
      },
      comboRecommendations: {
        pair: [{
          treatment: "Semaglutide",
          category: "Weight Management",
          why: "Primary lever for fat-loss outcomes.",
          howItWorks: "GLP-1",
          priceEstimate: "$350/mo"
        }, {
          treatment: "B12 + MIC",
          category: "Muscle Support",
          why: "Balances weight-loss protocol with lean-mass support.",
          howItWorks: "Metabolic support",
          priceEstimate: "$180/mo"
        }],
        alternatives: []
      },
      additionalWellness: []
    },
    wellnessTransformationComplete: true,
    hasLashBrowAfterImage: false,
    isAutomatedOutreachEnabled: true,
    specifiedLanguageCode: "en_US"
  };
}

/**
 * The experimentation pipeline's end-to-end check on a snap: evaluating the smoke experiment
 * reports its exposure (once analytics is granted), and the row shows the arm assigned. Rendered
 * inside the sheet, so only someone who opens the panel is exposed.
 */
function ExperimentSmokeTestRow() {
  const $ = _c(3);
  const {
    variant,
    isReady
  } = useExperiment(EXPERIMENTS.SMOKE_E2E);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="font-medium">Experiment smoke test</div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const t1 = isReady ? variant : "loading\u2026";
  let t2;
  if ($[1] !== t1) {
    t2 = <div className="type-caption mt-4 rounded-md border border-dashed p-3">{t0}<div>{EXPERIMENTS.SMOKE_E2E}: {t1}</div></div>;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
function getDefaultsFromPathname(pathname: string | null): DebugAssessmentForm {
  const fallback: DebugAssessmentForm = {
    lang: "en-US",
    subdomainSlug: "",
    flowSlug: "try-on",
    stepSlug: "start"
  };
  if (!pathname) return fallback;
  const segments = pathname.split("/").filter(Boolean);
  // Support both canonical /a/ and legacy /assessment/ paths.
  let baseIdx = segments.indexOf("a");
  if (baseIdx !== 1) baseIdx = segments.indexOf("assessment");

  // Expected: /:lang/(a|assessment)/:subdomainSlug/:flow/:step
  if (baseIdx !== 1) return fallback;
  const lang = segments[0] ?? fallback.lang;
  const subdomainSlug = segments[baseIdx + 1] ?? fallback.subdomainSlug;
  const flowSlug = segments[baseIdx + 2] ?? fallback.flowSlug;
  const stepSlug = segments[baseIdx + 3] ?? fallback.stepSlug;
  const resolvedFlow = resolveFlowType(flowSlug);
  const normalizedFlowSlug = resolvedFlow === AgelessAssessmentFlowType.AestheticsWellness ? "transformation" : resolvedFlow === AgelessAssessmentFlowType.Wellness ? "wellness" : "try-on";
  return {
    lang,
    subdomainSlug,
    flowSlug: normalizedFlowSlug,
    stepSlug
  };
}